import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  MessageSquare, 
  Calendar,
  FileText,
  Users,
  Star,
  Globe,
  BookOpen,
  Clock,
  CheckCircle,
  ArrowRight,
  Mail,
  MapPin,
  Shield
} from 'lucide-react';

const ParentPartnership = () => {
  const communicationMethods = [
    {
      icon: MessageSquare,
      title: "Daily Updates",
      description: "Receive detailed information about your child's day, including activities, meals, naps, and special moments.",
      frequency: "Every day at pickup"
    },
    {
      icon: FileText,
      title: "Developmental Evaluations",  
      description: "Comprehensive assessments of your child's growth and development with personalized observations.",
      frequency: "Bi-annually"
    },
    {
      icon: Mail,
      title: "Monthly Newsletters",
      description: "Stay informed about center news, upcoming events, policy updates, and learning themes.",
      frequency: "Monthly"
    },
    {
      icon: Calendar,
      title: "Regular Meetings",
      description: "Scheduled opportunities to discuss your child's progress, concerns, and celebrate achievements.",
      frequency: "As needed"
    }
  ];

  const partnershipValues = [
    {
      icon: Heart,
      title: "Family Culture & Traditions",
      description: "We value and incorporate your family's culture, language, and traditions into our program. Share your heritage through stories, celebrations, and classroom contributions."
    },
    {
      icon: Users,
      title: "Active Participation",
      description: "Families are encouraged to be active participants in their child's learning journey through volunteering, special events, and sharing expertise or interests."
    },
    {
      icon: Globe,
      title: "Inclusive Community",
      description: "We celebrate diversity and create an inclusive environment where all families feel welcomed, respected, and valued for their unique perspectives."
    },
    {
      icon: BookOpen,
      title: "Collaborative Learning",
      description: "When families and educators work together, children thrive. We share observations, strategies, and goals to support consistent development."
    }
  ];

  const attendanceExpectations = [
    "Notify the center by 8:30 AM if your child will be absent",
    "Inform us of communicable illnesses for health tracking",  
    "Maintain regular attendance to support routine and friendships",
    "Provide advance notice for planned absences or vacations",
    "Keep emergency contact information current",
    "Communicate changes in pickup arrangements in writing"
  ];

  const withdrawalProcess = [
    {
      requirement: "Written Notice",
      details: "Two full calendar months' written notice is required for withdrawal",
      icon: FileText
    },
    {  
      requirement: "Deposit Return",
      details: "Deposit will be credited to your final month with proper notice",
      icon: CheckCircle
    },
    {
      requirement: "Transition Support", 
      details: "We support smooth transitions to new care arrangements with documentation and communication",
      icon: Heart
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-green-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="bg-maple-red text-white mb-6">
              🍁 Parent Partnership
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-navy-primary mb-6">
              Building Strong Family Partnerships
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              We believe that strong, respectful partnerships with families are essential to a child's success. 
              Maple Kids Academy promotes open communication, family involvement, and collaborative support 
              for each child's unique developmental journey.
            </p>
          </div>
        </div>
      </section>

      {/* Communication Methods */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
              How We Stay Connected
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Open communication is the foundation of our partnership with families. 
              We use multiple methods to keep you informed and involved in your child's daily experiences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {communicationMethods.map((method, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-primary">
                <CardHeader className="pb-4">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                      <method.icon className="h-6 w-6 text-blue-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-navy-primary">{method.title}</CardTitle>
                      <Badge variant="outline" className="text-xs mt-2">{method.frequency}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-gray-600 leading-relaxed">
                    {method.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 mb-6">
              Families are always welcome to meet with educators to discuss concerns, 
              share feedback, or collaborate on support plans.
            </p>
            <Button asChild className="bg-blue-primary hover:bg-blue-600">
              <Link to="/contact">
                Schedule a Meeting
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Partnership Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
              What We Value in Our Partnership
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every family brings unique strengths, perspectives, and experiences that enrich 
              our learning community. We celebrate these differences and create meaningful connections.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {partnershipValues.map((value, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 bg-white border-0 shadow-md">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-green-50 rounded-xl group-hover:scale-110 transition-transform">
                      <value.icon className="h-8 w-8 text-blue-primary" />
                    </div>
                    <CardTitle className="text-xl text-navy-primary">{value.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-gray-600 leading-relaxed">
                    {value.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Attendance & Communication Expectations */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
                Attendance & Communication
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Clear and timely communication helps us create a safe and supportive environment for all. 
                Regular attendance supports your child's routine, friendships, and overall development.
              </p>

              <div className="space-y-4 mb-8">
                {attendanceExpectations.map((expectation, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-primary mt-1 flex-shrink-0" />
                    <p className="text-gray-700">{expectation}</p>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <h4 className="font-semibold text-navy-primary mb-3">Why This Matters</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Consistent attendance and communication help us plan staffing, meals, and activities effectively. 
                  It also ensures your child can fully participate in group activities and maintain important friendships.
                </p>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/1449934/pexels-photo-1449934.jpeg" 
                alt="Professional childcare setting showing family partnership" 
                className="rounded-2xl shadow-xl w-full h-96 object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-rainbow-blue text-white p-4 rounded-full shadow-lg">
                <Calendar className="h-8 w-8" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Withdrawal Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
              Withdrawal & Transition Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand that circumstances change and will always support your child's transition 
              with care and professionalism when it's time to move on.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {withdrawalProcess.map((step, index) => (
              <Card key={index} className="bg-white/80 backdrop-blur-sm border-0 shadow-lg text-center">
                <CardContent className="p-6">
                  <div className="p-4 bg-blue-50 rounded-full w-fit mx-auto mb-4">
                    <step.icon className="h-8 w-8 text-blue-primary" />
                  </div>
                  <h3 className="font-semibold text-navy-primary mb-3">{step.requirement}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.details}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="max-w-3xl mx-auto mt-12">
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-center text-navy-primary">Transition to Other Programs</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600 leading-relaxed mb-6">
                  We support children and families as they prepare to transition to preschool or other child care settings. 
                  This includes sharing observations, preparing transition documents, and speaking directly with receiving 
                  educators when permission is given.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-navy-primary">What We Provide:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Developmental assessments and observations</li>
                      <li>• Documentation of learning and growth</li>
                      <li>• Communication with new educators</li>
                      <li>• Family meetings to discuss transition</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-navy-primary">For Your Child:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Books and visuals about changes</li>
                      <li>• Classroom conversations about next steps</li>
                      <li>• Emotional support during transition</li>
                      <li>• Celebration of their growth and achievements</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-purple-600 to-pink-500 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Come Visit Our Happy Place!
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <MapPin className="h-5 w-5 mt-1 flex-shrink-0 text-yellow-300" />
                  <div>
                    <p className="font-bold text-lg">10681 248 St, Maple Ridge, BC</p>
                    <p className="text-purple-100">Easy parking for families</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <Clock className="h-5 w-5 mt-1 flex-shrink-0 text-green-300" />
                  <div>
                    <p className="font-bold text-lg">Monday - Friday: 7:00 AM - 5:00 PM</p>
                    <p className="text-purple-100">Closed weekends and holidays</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-yellow-400 hover:bg-yellow-500 text-purple-800 font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  <Link to="/contact">Book Your Visit</Link>
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-white/20 hover:bg-white/30 text-white border-2 border-white hover:border-yellow-300 font-bold px-8 py-4 rounded-full transition-all"
                >
                  <Link to="/enroll">Join Our Family</Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/20 backdrop-blur-sm p-8 rounded-2xl border-2 border-white/30">
                <h3 className="text-2xl font-bold mb-6 text-center">
                  Why Choose Maple Kids?
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                      <Users className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Small groups, big love</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-400 rounded-full flex items-center justify-center">
                      <Shield className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Licensed & insured</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-pink-400 rounded-full flex items-center justify-center">
                      <Heart className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Every child is welcome</span>
                  </div>
                </div>
                
                <div className="mt-6 text-center bg-white/20 p-4 rounded-xl">
                  <p className="text-sm font-medium">Ready to join our family?</p>
                  <p className="text-xs text-purple-100 mt-1">Tours available by appointment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ParentPartnership;